'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { Button } from '../../components/ui/button';
import {
  DollarSign,
  Clock,
  GraduationCap,
  Globe,
  ArrowRight,
  BriefcaseBusiness,
} from 'lucide-react';
import ApplyJobForm from './apply-job-form';

export default function Careers() {
  const [showForm, setShowForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const jobs = [
    {
      id: 1,
      title: 'Real Estate Agent - (Secondary Market)',
      link: '/careers/agent-position',
      description:
        'Join X-Space Real Estate as a Real Estate Agent and help clients find their dream properties while earning up to 80% commission in a supportive, growth-oriented environment.',
      requirements: [
        'Valid RERA license with minimum 1 year of UAE real estate experience',
        'Fluent in English (additional languages are a plus)',
        'Go-getter attitude and dedication to exceeding targets',
        'Valid UAE driving license',
        'Ability to work with company-provided leads and CRM systems',
        'Comfortable creating social media content for property promotion (videos/posts)',
      ],
      location: 'Dubai, UAE',
      type: 'Full-time',
    },
    // {
    //   id: 2,
    //   title: 'Sales Manager',
    //   link: '/careers/salemanager-position',
    //   description:
    //     'Lead our sales team to success and drive business growth in the Dubai real estate market.',
    //   requirements: [
    //     'Valid RERA Broker card',
    //     '2+ years experience',
    //     'Strong leadership skills',
    //     'Market knowledge',
    //   ],
    //   location: 'Dubai, UAE',
    //   type: 'Full-time',
    // },
    {
      id: 3,
      title: 'Off-Plan Specialist | Dubai | 80% + Commission',
      link: '/careers/offplan-agent-position',
      description:
        'Join our fast-growing Dubai team as an Off-Plan Specialist and unlock uncapped earnings while working with exclusive inventory and a daily flow of qualified leads.',
      requirements: [
        'Experienced brokers ready to earn more and grow faster',
        'Fresh agents eager to learn & dominate the Dubai market',
        'Strong communicators with natural sales energy',
        'Fluent in English (other languages a plus)',
        'UAE Driving License preferred',
      ],
      location: 'Dubai, UAE',
      type: 'Full-time',
    },
    {
      id: 4,
      title: 'Property Consultant | Dubai',
      link: '/careers/property-consultant-position',
      description:
        'Join X-Space Real Estate as a Property Consultant and work full-time in-house to generate leads, engage clients, create content, and help grow our brand while earning a competitive salary and commission.',
      requirements: [
        'Experience in real estate sales or telesales preferred',
        'Strong communication & relationship-building skills',
        'Ability to create social media content (video/posts)',
        'Strong administrative skills (CRM, reporting)',
        'Proficiency in MS Office, CRM, social media',
        'Fluent in English (other languages are a plus)',
        'Ambitious, target-driven, and disciplined',
      ],
      location: 'Dubai, UAE',
      type: 'Full-time',
    },
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  return (
    <div className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>
      {/* Hero Section */}
      <section className='relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20'>
        {/* Background with animated gradient */}
        <div className='absolute inset-0 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 z-0'>
          <motion.div
            className='absolute inset-0 opacity-30 dark:opacity-40'
            animate={{
              background: [
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 80% 70%, rgba(56, 245, 239, 0.15) 0%, rgba(254, 42, 83, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
                'radial-gradient(circle at 20% 30%, rgba(254, 42, 83, 0.15) 0%, rgba(56, 245, 239, 0.05) 50%, rgba(0, 0, 0, 0) 100%)',
              ],
            }}
            transition={{
              duration: 15,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* Decorative elements */}
        <div className='absolute inset-0 overflow-hidden z-0'>
          {/* Top left circle */}
          <motion.div
            className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#fe2a53]/5 dark:bg-[#fe2a53]/10'
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 20,
              ease: 'easeInOut',
            }}
          />

          {/* Bottom right circle */}
          <motion.div
            className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-[#38f5ef]/5 dark:bg-[#38f5ef]/10'
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -10, 0],
            }}
            transition={{
              repeat: Number.POSITIVE_INFINITY,
              duration: 25,
              ease: 'easeInOut',
            }}
          />
        </div>

        <div className='container mx-auto px-4 z-10 py-20'>
          <div className='max-w-7xl mx-auto'>
            {/* Section title with animated underline */}
            <motion.div
              className='text-center mb-16'
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h1
                className='text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-[#fe2a53] to-[#38f5ef] bg-clip-text text-transparent'
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Careers
              </motion.h1>
              <motion.div
                className='h-1 w-24 bg-gradient-to-r from-[#fe2a53] to-[#38f5ef] mx-auto'
                initial={{ width: 0 }}
                animate={{ width: 120 }}
                transition={{ duration: 1, delay: 0.5 }}
              />
              <motion.p
                className='mt-6 text-xl text-gray-600 dark:text-gray-400 max-w-4xl mx-auto'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                At X-Space Real Estate, we offer a dynamic work environment that
                fosters growth and innovation. Join us to be part of a team
                where your talents are valued, and your career aspirations are
                supported.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className='py-20 px-4 bg-gray-50 dark:bg-gray-900 relative overflow-hidden'>
        {/* Luxury pattern background */}
        <div className='absolute inset-0 opacity-5 dark:opacity-10'>
          <motion.div
            className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{
              duration: 50,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: 'reverse',
            }}
          />
        </div>

        <div className='container mx-auto relative z-10'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
              itemProp='why work with us'
            >
              Why Work With Us?
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-[#fe2a53] to-[#38f5ef] mx-auto mb-6'
            />
          </motion.div>

          {/* Benefits Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto'>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 flex items-center gap-6'
            >
              <div className='flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-r from-[#fe2a53]/20 to-[#38f5ef]/20 flex items-center justify-center'>
                <DollarSign className='w-8 h-8 text-[#fe2a53] dark:text-[#38f5ef]' />
              </div>
              <div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  Competitive Commission and Salary
                </h3>
                <p className='text-gray-600 dark:text-gray-400'>
                  Earn up to 80% commission on deals with our industry-leading
                  compensation structure.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 flex items-center gap-6'
            >
              <div className='flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-r from-[#fe2a53]/20 to-[#38f5ef]/20 flex items-center justify-center'>
                <Clock className='w-8 h-8 text-[#fe2a53] dark:text-[#38f5ef]' />
              </div>
              <div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  Flexible Hours and Vacations
                </h3>
                <p className='text-gray-600 dark:text-gray-400'>
                  Enjoy work-life balance with flexible scheduling and generous
                  vacation policies.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 flex items-center gap-6'
            >
              <div className='flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-r from-[#fe2a53]/20 to-[#38f5ef]/20 flex items-center justify-center'>
                <GraduationCap className='w-8 h-8 text-[#fe2a53] dark:text-[#38f5ef]' />
              </div>
              <div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  Learn and Develop Your Skills
                </h3>
                <p className='text-gray-600 dark:text-gray-400'>
                  Access continuous training and professional development
                  opportunities to grow your career.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 flex items-center gap-6'
            >
              <div className='flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-r from-[#fe2a53]/20 to-[#38f5ef]/20 flex items-center justify-center'>
                <Globe className='w-8 h-8 text-[#fe2a53] dark:text-[#38f5ef]' />
              </div>
              <div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                  Meet New Cultures and Enjoy
                </h3>
                <p className='text-gray-600 dark:text-gray-400'>
                  Experience a diverse workplace with colleagues and clients
                  from around the world.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Opportunities Section */}
      <section className='py-20 px-4'>
        <div className='container mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='text-center mb-16'
          >
            <h3
              className='text-4xl font-bold text-gray-900 dark:text-white mb-4'
              itemProp='careers opportunities'
            >
              Career Opportunities
            </h3>
            <div className='w-24 h-1 bg-gradient-to-r from-[#fe2a53] to-[#38f5ef] mx-auto mb-6'></div>
            <p className='text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto'>
              Explore exciting job opportunities and take the next step in your
              career with X-Space Real Estate.
            </p>
          </motion.div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto'>
            {jobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden'
              >
                <div className='relative'>
                  <div className='absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#fe2a53] to-[#38f5ef]'></div>
                  <div className='p-8'>
                    <div className='flex items-start justify-between mb-4'>
                      <div>
                        <h3
                          className='text-2xl font-bold text-gray-900 dark:text-white mb-2 flex flex-wrap'
                          itemProp={job.title}
                        >
                          {job.title}
                        </h3>
                        <div className='flex items-center text-sm text-gray-600 dark:text-gray-400 mb-4'>
                          <span className='flex items-center mr-4'>
                            <BriefcaseBusiness className='w-4 h-4 mr-2' />
                            {job.type}
                          </span>
                          <span>{job.location}</span>
                        </div>
                      </div>
                    </div>

                    <p className='text-gray-600 dark:text-gray-400 mb-6'>
                      {job.description}
                    </p>

                    <div className='mb-6'>
                      <h4 className='font-semibold text-gray-900 dark:text-white mb-2'>
                        Requirements:
                      </h4>
                      <ul className='space-y-1'>
                        {job.requirements.map((req, i) => (
                          <li key={i} className='flex items-start'>
                            <span className='text-[#fe2a53] dark:text-[#38f5ef] mr-2'>
                              •
                            </span>
                            <span className='text-gray-600 dark:text-gray-400'>
                              {req}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      rel='preload'
                      href={job.link}
                      className='inline-flex items-center text-[#fe2a53] dark:text-[#38f5ef] hover:underline'
                    >
                      View Full Job Description
                      <ArrowRight className='w-4 h-4 ml-1' />
                    </Link>
                    <Button
                      onClick={() => handleApplyClick(job)}
                      className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white px-6 py-3 rounded-full float-right'
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className='py-16 px-4 mb-16'>
        <div className='container mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#fe2a53]/10 to-[#38f5ef]/10 dark:from-[#fe2a53]/20 dark:to-[#38f5ef]/20 shadow-xl'
          >
            {/* Luxury pattern background with animation */}
            <div className='absolute inset-0 opacity-5 dark:opacity-10'>
              <motion.div
                className="absolute inset-0 bg-[url('https://images.x-space.ae/assets/images/pattern.png')]"
                animate={{
                  backgroundPosition: ['0% 0%', '100% 100%'],
                }}
                transition={{
                  duration: 30,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: 'reverse',
                }}
              />
            </div>

            {/* Gold corner accents */}
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl z-10'></div>
            <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-3xl z-10'></div>
            <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-3xl z-10'></div>
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37] rounded-br-3xl z-10'></div>

            <div className='p-12 relative z-10 text-center'>
              <motion.h4
                className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Ready to Join Our{' '}
                <span clasname='text-[#D4AF37]'>Dynamic Team</span>?
              </motion.h4>
              <motion.p
                className='text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Take the next step in your career with X-Space Real Estate.
                We're looking for passionate individuals who are ready to make
                an impact in Dubai's thriving real estate market.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <Button
                  onClick={() => {
                    setSelectedJob(null);
                    setShowForm(true);
                  }}
                  className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white px-8 py-6 text-lg rounded-full'
                >
                  Apply Now
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Job Application Form Modal */}
      {showForm && (
        <ApplyJobForm onClose={handleCloseForm} jobTitle={selectedJob?.title} />
      )}
    </div>
  );
}
